import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { Link } from '@dbt-labs/sourdough';

import { AccessLevel } from '@/__generated__/graphql';
import { PaginatedTable } from '@/components/PaginatedTable';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDiscoveryAppliedModels } from '@/hooks/discovery/appliedModels';
import { PublicModelResult, usePublicModels } from '@/hooks/discovery/publicModels';
import { useLinkToExplorer } from '@/hooks/links';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { useProducedPublicModelsColumns } from '../hooks/useProducedPublicModelsColumns';
const PAGE_SIZE = 5;

export const ProducedPublicModels = () => {
  const { accountId, projectId, environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const getModelFilterUrl = useLinkToExplorer('resourceFilter');
  const navigate = useNavigate();
  const getPublicModelsLink = useLinkToExplorer('multiProject');
  const producedPublicModels = useDiscoveryAppliedModels({
    first: PAGE_SIZE,
    environmentId,
    userId,
    filter: {
      access: AccessLevel.Public,
    },
    includeQueryCount: false,
  });
  const producedPublicModelIds =
    producedPublicModels.data?.map((model) => model.uniqueId) ?? [];
  const publicModels = usePublicModels({
    accountId,
    first: PAGE_SIZE,
    environmentIds: [environmentId],
    uniqueIds: producedPublicModelIds,
    includeDependentProjects: true,
  });

  const hydratedModels = useMemo(() => {
    const publicModelsMap = publicModels.data?.reduce(
      (acc, model) => {
        acc[model.uniqueId] = model;
        return acc;
      },
      {} as Record<string, PublicModelResult | undefined>,
    );
    return producedPublicModels.data?.map(({ __typename, ...model }) => {
      return {
        ...model,
        dependentProjects: publicModelsMap?.[model.uniqueId]?.dependentProjects,
      };
    });
  }, [producedPublicModels.data, publicModels.data]);

  const disableGroupsColumn =
    !!producedPublicModels.data &&
    !producedPublicModels.data.some((model) => !!model.group);
  const columns = useProducedPublicModelsColumns({ disableGroupsColumn });

  return (
    <div>
      <p className="mb-2 text-fgMain">
        Discover all of your public models accross the entire account.&nbsp;
        <Link isInternal to={getPublicModelsLink({ accountId })}>
          See all public models.
        </Link>
      </p>
      <PaginatedTable
        data={hydratedModels ?? []}
        isLoading={producedPublicModels.fetching || publicModels.fetching}
        maxResultCount={undefined}
        pageSize={PAGE_SIZE}
        pagination={{
          loadMoreLabel: 'See All Public Models In This Project',
          hasMorePages: true,
          isPageLoading: producedPublicModels.fetching,
          onLoadMore: () => {
            navigate(
              getModelFilterUrl({
                accountId,
                projectId,
                environmentType: 'production',
                resourceType: 'model',
                access: 'public',
              }),
            );
          },
        }}
        emptyStateProps={{
          header: `There are no public models produced by this project`,
        }}
        columns={columns}
      />
    </div>
  );
};
